import {createPortError,safePortCode} from './port-error.js';

const OAUTH_PROVIDERS=new Set(['google','apple']);

function requireAuthClient(client){
  if(!client?.auth)throw new TypeError('Supabase auth client is required');
  for(const method of ['getSession','onAuthStateChange','signOut']){
    if(typeof client.auth[method]!=='function')throw new TypeError(`Supabase auth.${method}() is required`);
  }
  return client.auth;
}

function normaliseEmail(value){
  const email=String(value||'').trim().toLowerCase();
  if(!/^\S+@\S+\.\S+$/.test(email))throw new TypeError('A valid email address is required');
  return email;
}

function normaliseTokenHash(value){
  const tokenHash=String(value||'').trim();
  if(!tokenHash||tokenHash.length>2048)throw new TypeError('A valid magic-link token hash is required');
  return tokenHash;
}

function report(diagnostics,scope,error,meta={}){
  try{diagnostics?.capture?.(scope,error,meta);}catch{}
}

function fail(diagnostics,scope,code,error,{retryable=true}={}){
  report(diagnostics,scope,error,{
    code,
    providerCode:safePortCode(error?.code||error?.name,'PROVIDER_FAILURE')
  });
  throw createPortError(code,{retryable});
}

export function normaliseAuthSession(session){
  if(session==null)return null;
  const userId=String(session?.user?.id||'').trim();
  if(!userId)throw createPortError('AUTH_INVALID_SESSION');
  const rawExpiry=session?.expires_at??session?.expiresAt;
  const expires=rawExpiry==null||rawExpiry===''?null:Number(rawExpiry);
  return Object.freeze({
    user:Object.freeze({id:userId}),
    expiresAt:Number.isFinite(expires)?expires:null
  });
}

export function supabaseAuthOptions(){
  return Object.freeze({
    persistSession:true,
    autoRefreshToken:true,
    detectSessionInUrl:true,
    flowType:'pkce'
  });
}

export function createAuthAdapter({client,diagnostics}={}){
  const auth=requireAuthClient(client);

  async function getSession(){
    const {data,error}=await auth.getSession();
    if(error)fail(diagnostics,'auth.get-session','AUTH_SESSION_FAILED',error);
    return normaliseAuthSession(data?.session);
  }

  function onSessionChange(listener){
    if(typeof listener!=='function')throw new TypeError('Session listener is required');
    const result=auth.onAuthStateChange((event,session)=>listener(Object.freeze({
      event:String(event||'SESSION_CHANGED'),
      session:normaliseAuthSession(session)
    })));
    const subscription=result?.data?.subscription;
    return ()=>subscription?.unsubscribe?.();
  }

  async function signInWithMagicLink(email,{redirectTo}={}){
    if(typeof auth.signInWithOtp!=='function')throw new TypeError('Supabase auth.signInWithOtp() is required');
    const options=redirectTo?{emailRedirectTo:String(redirectTo)}:undefined;
    const {error}=await auth.signInWithOtp({email:normaliseEmail(email),...(options?{options}: {})});
    if(error)fail(diagnostics,'auth.magic-link','AUTH_MAGIC_LINK_FAILED',error);
    return Object.freeze({ok:true,status:'magic_link_requested'});
  }

  async function verifyMagicLinkTokenHash(tokenHash){
    if(typeof auth.verifyOtp!=='function')throw new TypeError('Supabase auth.verifyOtp() is required');
    const {data,error}=await auth.verifyOtp({token_hash:normaliseTokenHash(tokenHash),type:'email'});
    if(error)fail(diagnostics,'auth.magic-link.verify','AUTH_MAGIC_LINK_VERIFY_FAILED',error);
    return normaliseAuthSession(data?.session);
  }

  async function signInWithOAuth(provider,{redirectTo,scopes}={}){
    if(typeof auth.signInWithOAuth!=='function')throw new TypeError('Supabase auth.signInWithOAuth() is required');
    const selected=String(provider||'').toLowerCase();
    if(!OAUTH_PROVIDERS.has(selected))throw new TypeError(`Unsupported OAuth provider: ${selected||'empty'}`);
    const options={};
    if(redirectTo)options.redirectTo=String(redirectTo);
    if(scopes)options.scopes=String(scopes);
    const {error}=await auth.signInWithOAuth({provider:selected,...(Object.keys(options).length?{options}:{})});
    if(error)fail(diagnostics,`auth.oauth.${selected}`,'AUTH_OAUTH_FAILED',error);
    return Object.freeze({ok:true,status:'oauth_started',provider:selected});
  }

  async function signOut(scope){
    const {error}=await auth.signOut({scope});
    if(error)fail(diagnostics,`auth.sign-out.${scope}`,'AUTH_SIGN_OUT_FAILED',error);
    return Object.freeze({ok:true,scope});
  }

  return Object.freeze({
    getSession,
    onSessionChange,
    signInWithMagicLink,
    verifyMagicLinkTokenHash,
    signInWithOAuth,
    signOutLocal:()=>signOut('local'),
    signOutGlobal:()=>signOut('global')
  });
}
